import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { ClassEntityService } from './entity-services/classes-entity.service';
import { Log, IShortLogger } from './logger.service';
import { NotificationService } from './notification.service';

/**
 * Launch context types for the canonical VM session contexts.
 *
 * The string literal values are the canonical context names used in:
 *   - The SHA canonical key (e.g. `org-template-vm:{userId}:{templateId}`)
 *   - The `VmContextPayload.context` field stored in localStorage
 *   - The `PrepareLaunchRequest.Context` field sent to the backend
 *   - The `VmSession.Context` field in MongoDB
 *
 */
export type LaunchContext =
  | 'org-template-vm'
  | 'class-template-vm'
  | 'participant-self-vm'
  | 'operator-remote-vm';

/**
 * Launch details response from backend (standardized across all contexts)
 */
export interface LaunchDetailsResponse {
  encryptedToken: string;
  cloudUserName: string;
  password: string;
  protocol?: 'rdp' | 'vnc';
  // Optional fields for specific contexts
  templateId?: string;
  resourceId?: string;
  hostname?: string;
}

/**
 * Launch parameters for unified launch method
 */
export interface VmLaunchParams {
  /** Primary identifier (classId, templateId, etc.) */
  primaryId: string;
  /** Secondary identifier (userId, templateId, etc.) */
  secondaryId: string;
  /** Launch context for endpoint selection */
  context: LaunchContext;
  /** Optional user role for authorization */
  userRole?: string;
  /** Optional username for logging */
  userName?: string;
  /** Optional target user name for trainer remote access */
  targetUserName?: string;
  /** Optional actual userId for backend calls (when different from secondaryId) */
  actualUserId?: string;
  /**
   * Optional VM Connection Template id resolved at the call site (typically read from the
   * owning class document or the org-template resource document). Forwarded into the SHA-keyed
   * payload so vm-connect sends it to `POST /api/v1/launch/prepare` and the backend overlays
   * the template's Allow* flags onto the global appsettings.json defaults inside the encrypted
   * Guacamole token.
   */
  vmConnectionTemplateId?: string | null;
}

// ─── 2-phase modal launch types ─────────────────────────────────────────────
export type VmLaunchPhase = 'idle' | 'fetching' | 'ready' | 'error' | 'conflict';

export interface VmLaunchModalState {
  phase: VmLaunchPhase;
  params?: VmLaunchParams;
  errorMessage?: string;
  conflictContext?: string;
}

// ─── SHA-keyed context payload ───────────────────────────────────────────────
/**
 * Payload stored in localStorage under `vm_ctx_{sha}` and read by
 * VmConnectionComponent to determine which backend endpoint to call.
 *
 * mode === 'cached' — credentials already pre-fetched and stored in
 *   VmSessionManagerService under `sessionKey`; vm-connect reads them from there.
 *
 * mode === 'fetch'  — vm-connect must call the backend to obtain credentials
 * from POST /api/v1/launch/prepare.
 */
export interface VmContextPayload {
  mode: 'cached' | 'fetch';
  sessionKey: string;
  context: LaunchContext;

  // ── Identity ──────────────────────────────────────────────────────────────
  /** The authenticated user's ID — always the initiating user. */
  userId?: string;
  userName?: string;

  // ── Resource identifiers (context-dependent) ──────────────────────────────
  /** Class ID — required for class-template-vm, participant-self-vm, operator-remote-vm. */
  classId?: string;
  /** Template resource ID — required for org-template-vm and class-template-vm. */
  templateId?: string;
  /** Target participant user ID — required for operator-remote-vm. */
  studentUserId?: string;
  /** Target VM public IP — required for operator-remote-vm. */
  vmId?: string;

  // ── Pre-computed canonical SHA ────────────────────────────────────────────
  /**
   * SHA-256 digest of the canonical key string, pre-computed by the frontend.
   * Sent to the backend as `clientSha` for tamper-detection verification.
   * Undefined for legacy contexts that do not use the unified endpoint.
   */
  sessionSha?: string;

  /**
   * Optional per-class VM Connection Template ID. Forwarded to the backend's
   * `POST /api/v1/launch/prepare` endpoint as `vmConnectionTemplateId` so the resolved
   * template's Allow* flags overlay the global appsettings.json defaults inside the
   * encrypted Guacamole token. Omitted (or null) preserves the pre-template behaviour.
   */
  vmConnectionTemplateId?: string | null;
}

/**
 * Unified VM Launcher Service
 *
 * Provides a single, simple interface for launching VMs across all contexts:
 * - Class template VM: `launch('classId', 'templateId', 'class-template-vm')`
 * - Participant VM: `launch('classId', 'userId', 'participant-self-vm')`
 * - Operator remote VM: `launch('classId', 'studentUserId', 'operator-remote-vm')`
 *
 * All launches are handled with proper session caching and window management.
 */
@Injectable({
  providedIn: 'root'
})
export class UnifiedVmLauncherService {
  private readonly logger: IShortLogger<UnifiedVmLauncherService> = Log.create(UnifiedVmLauncherService);

  /**
  * Emits whenever a backend active-session check blocks a participant-self-vm launch.
   * Components subscribe to this to show a modal explaining the conflict instead of silently
   * ignoring the launch attempt.
   */
  public readonly sessionConflict$ = new Subject<{ classId: string; userId: string; activeContext: string }>();

  // ── Modal-driven 2-phase launch state ────────────────────────────────────
  private readonly _modalState$ = new BehaviorSubject<VmLaunchModalState>({ phase: 'idle' });
  readonly modalState$: Observable<VmLaunchModalState> = this._modalState$.asObservable();
  private _pendingParams: VmLaunchParams | null = null;

  constructor(
    private classEntityService: ClassEntityService,
    private notification: NotificationService
  ) {
    this.cleanupLegacyState();
  }

  /**
   * Clean up any leftover localStorage entries from the old implementation
   */
  private cleanupLegacyState(): void {
    const keys = Object.keys(localStorage);
    const removedKeys: string[] = [];

    keys.forEach(key => {
      if (key.startsWith('vm_window_active_')) {
        localStorage.removeItem(key);
        removedKeys.push(key);
      }
    });

    if (removedKeys.length > 0) {
      this.logger.d('Cleaned up legacy localStorage keys:', removedKeys);
    }
  }

  /**
   * Direct launch — opens a new tab immediately within the current user gesture.
  * Use for programmatic launches where the caller already has all required context.
   * where the caller already has all the necessary context before triggering.
   * For standard participant/template launches prefer prepareLaunch() which shows
   * a 2-phase modal so the tab opening happens in a dedicated user gesture.
   */
  async launch(params: VmLaunchParams): Promise<boolean> {
    const sessionKey = `${params.primaryId}-${params.secondaryId}`;
    const windowName = `vm_${sessionKey.replace(/[^a-zA-Z0-9]/g, '_')}`;
    // Open synchronously — this MUST stay before any await to preserve the
    // browser's trusted user-gesture token and avoid popup-blocker rejection.
    const preOpenedWindow = window.open('', windowName);
    this.logger.i(`🚀 DirectLaunch: ${sessionKey}`);

    // All contexts: always store a mode:'fetch' payload so the child window
    // calls the appropriate backend endpoint — the authoritative dedup gate.
    // Credentials are never pre-fetched or cached in localStorage; there is no
    // localStorage-tampering shortcut for any context.
    // Participant duplicate enforcement still has an early-exit
    // checkSessionStatus guard in prepareLaunch() for the conflict-modal UX.
    this.storeAndNavigateToKey(this.buildFetchContextPayload(sessionKey, params), preOpenedWindow);
    return true;
  }

  // ── 2-phase modal launch ──────────────────────────────────────────────────

  /**
   * Phase 1 — fetch credentials and show a modal.
   * Call this from button click handlers instead of launch().
   * The modal will transition to a "Ready" state with a "Connect" button;
   * clicking Connect calls executeLaunch() which opens the new tab inside
   * that user gesture, bypassing popup blockers completely.
   */
  prepareLaunch(params: VmLaunchParams): void {
    if (this._modalState$.value.phase !== 'idle') {
      this.logger.w('prepareLaunch: modal already active — ignoring duplicate call');
      return;
    }
    this._pendingParams = params;

    const sessionKey = `${params.primaryId}-${params.secondaryId}`;
    const windowName = `vm_${sessionKey.replace(/[^a-zA-Z0-9]/g, '_')}`;
    // Open blank window synchronously to preserve the browser's user-gesture token
    // and bypass popup blockers — MUST stay before any await or subscribe call.
    const preOpenedWindow = window.open('', windowName);

    // Show brief spinner while the session check runs.
    this._setModalState({ phase: 'fetching', params });

    // For participant-self-vm check for an active backend session first.
    const restrictedContexts: LaunchContext[] = ['participant-self-vm'];
    if (restrictedContexts.includes(params.context)) {
      const checkUserId = params.actualUserId || params.secondaryId;
      this.classEntityService.checkSessionStatus(params.primaryId, checkUserId).subscribe({
        next: (sessionStatus: any) => {
          if (sessionStatus?.isActive) {
            preOpenedWindow?.close();
            this.sessionConflict$.next({
              classId: params.primaryId,
              userId: checkUserId,
              activeContext: sessionStatus.context ?? ''
            });
            this._setModalState({
              phase: 'conflict',
              params,
              conflictContext: sessionStatus.context ?? ''
            });
          } else {
            this.storeAndNavigateToKey(this.buildFetchContextPayload(sessionKey, params), preOpenedWindow);
            this.dismissModal();
          }
        },
        // Session check failed — proceed with launch (non-blocking check)
        error: () => {
          this.storeAndNavigateToKey(this.buildFetchContextPayload(sessionKey, params), preOpenedWindow);
          this.dismissModal();
        }
      });
    } else {
      // For template/operator contexts: navigate immediately.
      // The child window calls the backend and handles any 409 conflict itself.
      this.storeAndNavigateToKey(this.buildFetchContextPayload(sessionKey, params), preOpenedWindow);
      this.dismissModal();
    }
  }

  /** Dismiss the modal and clear pending state without launching. */
  dismissModal(): void {
    this._pendingParams = null;
    this._setModalState({ phase: 'idle' });
  }

  // ─── Canonical-context launch helpers ────────────────────────────────────

  /**
   * Builds a `org-template-vm` payload with the canonical SHA pre-computed and
   * stores it in localStorage, then navigates the pre-opened VM window to the SHA key.
   *
   * @param params.userId         The authenticated user's ID
   * @param params.templateId     The org template resource ID
   * @param params.role           User role string
   * @param params.userName       Display name
   * @param preOpenedWindow       The tab opened synchronously in the caller's user gesture
   */
  async launchOrgTemplateVm(
    params: { userId: string; templateId: string; userName: string; vmConnectionTemplateId?: string | null },
    preOpenedWindow?: Window | null
  ): Promise<void> {
    const sha = await this.computeContextSha('org-template-vm', params.userId, params.templateId);
    const payload: VmContextPayload = {
      mode: 'fetch',
      sessionKey: `org-template-${params.userId}-${params.templateId}`,
      context: 'org-template-vm',
      userId: params.userId,
      templateId: params.templateId,
      userName: params.userName,
      sessionSha: sha,
      vmConnectionTemplateId: params.vmConnectionTemplateId ?? null,
    };
    localStorage.setItem(`vm_ctx_${sha}`, JSON.stringify(payload));
    this.launchVmWindowWithKey(sha, preOpenedWindow);
  }

  /**
   * Builds a `class-template-vm` payload with the canonical SHA pre-computed.
   */
  async launchClassTemplateVm(
    params: {
      userId: string; templateId: string; classId: string; userName: string;
      vmConnectionTemplateId?: string | null; uaTemplateId?: string | null;
    },
    preOpenedWindow?: Window | null
  ): Promise<void> {
    const sha = await this.computeContextSha(
      'class-template-vm', params.userId, params.templateId, params.classId
    );
    const payload: VmContextPayload = {
      mode: 'fetch',
      sessionKey: `class-template-${params.userId}-${params.templateId}-${params.classId}`,
      context: 'class-template-vm',
      userId: params.userId,
      templateId: params.templateId,
      classId: params.classId,
      userName: params.userName,
      sessionSha: sha,
      vmConnectionTemplateId: params.vmConnectionTemplateId ?? null,
    };
    localStorage.setItem(`vm_ctx_${sha}`, JSON.stringify(payload));
    if (params.uaTemplateId) {
      localStorage.setItem(`vm_ua_tpl_${sha}`, params.uaTemplateId);
    }
    this.launchVmWindowWithKey(sha, preOpenedWindow);
  }

  /**
   * Builds a `participant-self-vm` payload with the canonical SHA pre-computed.
   */
  async launchParticipantSelfVm(
    params: { userId: string; classId: string; userName: string; vmConnectionTemplateId?: string | null },
    preOpenedWindow?: Window | null
  ): Promise<void> {
    const sha = await this.computeContextSha('participant-self-vm', params.userId, params.classId);
    const payload: VmContextPayload = {
      mode: 'fetch',
      sessionKey: `participant-self-${params.userId}-${params.classId}`,
      context: 'participant-self-vm',
      userId: params.userId,
      classId: params.classId,
      userName: params.userName,
      sessionSha: sha,
      vmConnectionTemplateId: params.vmConnectionTemplateId ?? null,
    };
    localStorage.setItem(`vm_ctx_${sha}`, JSON.stringify(payload));
    this.launchVmWindowWithKey(sha, preOpenedWindow);
  }

  /**
   * Builds an `operator-remote-vm` payload with the canonical SHA pre-computed.
   */
  async launchOperatorRemoteVm(
    params: {
      operatorUserId: string; classId: string; studentUserId: string; vmId: string;
      userName: string; vmConnectionTemplateId?: string | null;
    },
    preOpenedWindow?: Window | null
  ): Promise<void> {
    const sha = await this.computeContextSha(
      'operator-remote-vm',
      params.operatorUserId, params.classId, params.studentUserId, params.vmId
    );
    const payload: VmContextPayload = {
      mode: 'fetch',
      sessionKey: `operator-remote-${params.operatorUserId}-${params.classId}-${params.studentUserId}`,
      context: 'operator-remote-vm',
      userId: params.operatorUserId,
      classId: params.classId,
      studentUserId: params.studentUserId,
      vmId: params.vmId,
      userName: params.userName,
      sessionSha: sha,
      vmConnectionTemplateId: params.vmConnectionTemplateId ?? null,
    };
    localStorage.setItem(`vm_ctx_${sha}`, JSON.stringify(payload));
    this.launchVmWindowWithKey(sha, preOpenedWindow);
  }

  private _setModalState(state: VmLaunchModalState): void {
    this._modalState$.next(state);
  }

  // ─── SHA-keyed navigation helpers ──────────────────────────────────────────

  // ─── SHA computation ────────────────────────────────────────────────────────

  /**
   * Computes a SHA-256 hex digest of the canonical colon-separated key for a context.
   *
   * The canonical key format per context is identical to the backend `VmSessionSha`:
   * - `org-template-vm:{userId}:{templateId}`
   * - `class-template-vm:{userId}:{templateId}:{classId}`
   * - `participant-self-vm:{userId}:{classId}`
   * - `operator-remote-vm:{operatorUserId}:{classId}:{studentUserId}:{vmId}`
   *
   * The backend re-derives the same SHA from the payload and rejects any mismatch,
   * so the order and content of `parts` must exactly match the backend constants.
   */
  async computeContextSha(context: string, ...parts: string[]): Promise<string> {
    const canonical = [context, ...parts].join(':');
    const bytes = new TextEncoder().encode(canonical);
    const hashBuffer = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
  * Convenience overload that accepts a full payload object and derives a stable
  * JSON-sorted SHA. Use `computeContextSha(context, ...parts)` for canonical
  * launch/session SHA verification.
   */
  async computeContextShaFromPayload(payload: object): Promise<string> {
    const canonical = JSON.stringify(
      Object.fromEntries(Object.entries(payload).sort(([a], [b]) => a.localeCompare(b)))
    );
    const bytes = new TextEncoder().encode(canonical);
    const hashBuffer = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Builds the `mode: 'fetch'` context payload from `VmLaunchParams`.
   * vm-connect reads this to know which backend endpoint to call.
   */
  private buildFetchContextPayload(sessionKey: string, params: VmLaunchParams): VmContextPayload {
    const launchUserId = params.actualUserId || params.secondaryId;
    return {
      mode: 'fetch',
      sessionKey,
      context: params.context,
      userId: launchUserId,
      classId: params.primaryId,
      templateId: params.context === 'class-template-vm' ? params.secondaryId : undefined,
      studentUserId: params.context === 'operator-remote-vm' ? params.secondaryId : undefined,
      vmId: undefined,
      userName: params.userName,
      vmConnectionTemplateId: params.vmConnectionTemplateId ?? null,
    };
  }

  /**
   * Computes a SHA key for `payload`, stores it in localStorage under
   * `vm_ctx_{sha}`, then navigates the (already-opened) VM tab to
   * `/#/vm-connect?key={sha}`.
   *
   * This must be called from within async/subscribe context — the pre-opened
   * window was already granted by the browser's synchronous user-gesture grant
   * earlier in `prepareLaunch()`, so navigating it here is safe.
   */
  private storeAndNavigateToKey(payload: VmContextPayload, preOpenedWindow?: Window | null): void {
    this.computeContextShaFromPayload(payload).then(sha => {
      localStorage.setItem(`vm_ctx_${sha}`, JSON.stringify(payload));
      this.launchVmWindowWithKey(sha, preOpenedWindow);
    });
  }

  /**
   * Navigates the VM tab to `/#/vm-connect?key={sha}` — a clean single-parameter
   * URL that carries no sensitive context.  If `preOpenedWindow` is still open,
   * it is navigated directly; otherwise a new tab is opened via `window.open`.
   */
  private launchVmWindowWithKey(sha: string, preOpenedWindow?: Window | null): void {
    const launchKey = `vm_launch_${sha}`;
    localStorage.setItem(launchKey, Date.now().toString());

    const fullUrl = `${window.location.origin}/#/vm-connect?key=${sha}`;
    this.logger.i('Navigating VM tab to SHA-keyed URL:', fullUrl);

    if (preOpenedWindow && !preOpenedWindow.closed) {
      preOpenedWindow.location.href = fullUrl;
      preOpenedWindow.focus();
      return;
    }

    const windowName = `vm_${sha.slice(0, 16)}`;
    const newWindow = window.open(fullUrl, windowName);
    if (!newWindow) {
      this.logger.e('Failed to open VM window — browser blocked the new tab');
      this.notification.notify(
        'Unable to open the VM in a new tab. Please allow this site to open new windows, then try again.',
        'error',
        5000
      );
    }
  }
}
